import React, { Fragment, useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
} from "@material-ui/core";
import {
  Button,
  useDataProvider,
  useRefresh,
  useUnselectAll,
} from "react-admin";
import { fetchAllData } from "../../../dataProvider";
import { delay } from "utils/tools";
import TableResultConfirmAdjustmentPrices from "./TableResultConfirmAdjustmentPrices";
import SaveIcon from "@material-ui/icons/Save";

interface IApproveAllButton {
  schoolId?: number;
  subcontractorId?: number;
}

interface IApproveResults {
  id: number;
  routeName: string;
  status: string;
}

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  cancelButton: {
    backgroundColor: "#eeeeee",
    color: "black",
  },
});

const ApproveAllButton = ({ schoolId, subcontractorId }: IApproveAllButton) => {
  const [open, setOpen] = useState(false);
  const [finished, setFinished] = useState(false);
  const [results, setResults] = useState<IApproveResults[]>([]);
  const [processing, setProcessing] = useState(false);
  const [school, setSchool] = useState<ISchool>();
  const [subcontractor, setSubcontractor] = useState<ISubcontractor>();

  const refresh = useRefresh();
  const unselectAll = useUnselectAll();
  const provider = useDataProvider();
  const classes = useStyles();

  /**
   * Get the school details if `schoolId` is present..
   * This is used to personalize the dialog title/message.
   */
  useEffect(() => {
    if (schoolId) {
      provider
        .getOne("schools", {
          id: schoolId,
        })
        .then(({ data }) => {
          setSchool(data as ISchool);
        });
    } else {
      setSchool(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  /**
   * Get the subcontractor details if `subcontractorId` is present..
   * This is used to personalize the dialog title/message.
   */
  useEffect(() => {
    if (subcontractorId) {
      provider
        .getOne("subcontractors", {
          id: subcontractorId,
        })
        .then(({ data }) => {
          setSubcontractor(data as ISubcontractor);
        });
    } else {
      setSubcontractor(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subcontractorId]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    if (finished) {
      unselectAll("subcontractorAdjustmentPrices");
      refresh();
    }

    setOpen(false);
  };

  /**
   * Approve an individual adjustment price record by ID.
   * This also updates the UI with success/failure status for each row.
   */
  const handleApprove = (
    selectedAdjustmentPrice: IApproveResults,
    adjustmentPrices: IApproveResults[],
    index: number,
  ) => {
    return new Promise((resolve, reject) => {
      provider
        .customUpdate(
          `subcontractorAdjustmentPrices/${selectedAdjustmentPrice.id}/approve`,
          {
            data: {},
          },
        )
        .then(() => {
          adjustmentPrices[index].status = "Approved";
          setResults([...adjustmentPrices]);

          resolve(true);
        })
        .catch((err: any) => {
          adjustmentPrices[index].status = `Error, ${err.message}`;
          setResults([...adjustmentPrices]);

          reject();
        });
    });
  };

  /**
   * Get all draft items based on filters, then approves them one by one.
   * Result will show in the table for each data.
   */
  const handleConfirm = async () => {
    setProcessing(true);

    // get all adjustment prices matching the filters
    const adjustmentPrices = await fetchAllData(
      "subcontractorAdjustmentPrices",
      {
        SchoolId: schoolId,
        SubcontractorId: subcontractorId,
        Status: "draft",
      },
    );

    const mappedAdjustmentPrices = adjustmentPrices.map((data) => ({
      id: data.id,
      routeName: data.routeName,
      status: data.status,
    }));

    // Loop over each draft and approve it sequentially
    for (let i = 0; i < mappedAdjustmentPrices.length; i++) {
      mappedAdjustmentPrices[i].status = "Loading...";
      setResults([...mappedAdjustmentPrices]);

      await handleApprove(mappedAdjustmentPrices[i], mappedAdjustmentPrices, i);
      await delay(2000);
    }

    setFinished(true);
    setProcessing(false);
  };

  /**
   * Helper to dynamically generate the dialog title suffix
   * depending on which filters are present.
   * e.g. "for XYZ Subcontractor in ABC School"
   */
  const suffix = `${subcontractor?.name ? ` for ${subcontractor.name}` : ""}${
    school?.name ? ` in ${school.name}` : ""
  }`;

  return (
    <Fragment>
      <Button
        label="Approve All"
        onClick={handleOpen}
        style={{ backgroundColor: "#ffbf00" }}
      />

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="lg"
      >
        <DialogTitle id="alert-dialog-title">
          {`Approve All The Draft Adjustment Prices${suffix}`}
        </DialogTitle>
        <DialogContent>
          {results.length > 0 ? (
            <TableResultConfirmAdjustmentPrices records={results} />
          ) : (
            <DialogContentText color="initial">
              {`Are you sure you want to approve all draft adjustment prices${suffix}?`}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          {!finished && (
            <Button
              onClick={handleConfirm}
              color="primary"
              label={`${processing ? "Processing..." : "Confirm"}`}
              disabled={processing}
            >
              <SaveIcon />
            </Button>
          )}
          <Button
            onClick={handleClose}
            color="secondary"
            label={`${finished ? "Finish" : "Back"}`}
            disabled={processing}
            className={classes.cancelButton}
          />
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default ApproveAllButton;
